import { useApolloClient } from '@apollo/react-hooks';
import XLSX from 'xlsx';

import { SYLLABUS_MODE } from 'Domains/syllabus';

import {
  getCourseSectionReport,
  getTKCourseSectionReport,
} from '../../GraphQL/queries/report.query';
import { formatSchoolTerm, alignClassCourseSectionsById } from '../utils';

export const SHEET_TYPE = {
  ATTENDANCE_RATE: 'attendanceRate',
  EXAMINATION_RATE: 'examinationRate',
  EXAMINATION_AVERAGE_RESULT: 'examinationAverageResult',
};

const SHEET_NAME = {
  attendanceRate: 'ภาพรวมการเข้าเรียน',
  examinationRate: 'ภาพรวมการเข้าสอบ',
  examinationAverageResult: 'ภาพรวมคะแนน',
};

export const convertCourseSectionReportToAOA = (courseSectionReport, sheetType, mode) => {
  const {
    schoolGrade: { shortName: schoolGrade },
    classes: rawClasses = [],
    courseSections = [],
  } = courseSectionReport;
  // Match each class's sections to the header columns by section id, not position.
  const classes = alignClassCourseSectionsById(rawClasses, courseSections);
  if (mode === SYLLABUS_MODE.TRADITIONAL) {
    return classes.map(klass => [
      `${schoolGrade}/${klass.classNo}`,
      ...klass.courseSections.map(courseSection => courseSection?.[sheetType] ?? null),
    ]);
  }
  return classes.map(klass => [
    `${schoolGrade}/${klass.classNo}`,
    ...klass.courseSections.flatMap(courseSection => {
      if (!courseSection?.[sheetType]) {
        return ['', '', '', ''];
      }
      return Object.values(courseSection[sheetType]).map(value => {
        return value;
      });
    }),
  ]);
};

const formatReportToAOAHeader = (courseSectionReport, mode) => {
  const {
    course: { courseTitle },
    courseSections = [],
  } = courseSectionReport;

  const courseSectionNames = courseSections.map(courseSection => courseSection.courseSectionTitle);
  if (mode === SYLLABUS_MODE.TRADITIONAL) {
    return [
      ['', ...Array(courseSections.length).fill(courseTitle)],
      ['ห้อง', ...courseSectionNames],
    ];
  }

  const duplicateCourseSectionNamesForAOAHeader = courseSectionNames.flatMap(courseName =>
    Array(4).fill(courseName),
  );
  const statTypeName = ['ค่าต่ำสุด', 'ค่าสูงสุด', 'ค่าเฉลี่ย', 'มัธยฐาน'];
  const duplicateStatTypeNameForAOAHeader = courseSectionNames.flatMap(() => statTypeName);
  return [
    ['', ...Array(duplicateStatTypeNameForAOAHeader.length).fill(courseTitle)],
    ['', ...duplicateCourseSectionNamesForAOAHeader],
    ['ห้อง', ...duplicateStatTypeNameForAOAHeader],
  ];
};

const createAndFormatWorksheet = (formattedReportAOA, mode, courseSectionReport) => {
  const worksheet = XLSX.utils.aoa_to_sheet(formattedReportAOA);
  const cols = [
    { wch: 10 }, // ห้อง
    ...Array(formattedReportAOA[0].length - 1).fill({ wch: 15 }), // หน่วยการเรียนรู้
  ];
  worksheet['!merges'] = [
    { s: { r: 0, c: 1 }, e: { r: 0, c: cols.length - 1 } }, // ชื่อวิชา
  ];
  if (mode === SYLLABUS_MODE.TUNGKAI) {
    const { courseSections = [] } = courseSectionReport;
    const courseSectionNames = courseSections.map(
      courseSection => courseSection.courseSectionTitle,
    );
    let sectionStart = 1;
    for (const sectionName of courseSectionNames) {
      worksheet['!merges'].push({ s: { r: 1, c: sectionStart }, e: { r: 1, c: sectionStart + 3 } });
      sectionStart += 4;
    }
  }
  worksheet['!cols'] = cols;
  return worksheet;
};

const ExportCourseSectionReportData = props => {
  const { schoolTermId, schoolGradeId, subjectId, courseCode, schoolCode, mode } = props;

  const SHEETS =
    mode === SYLLABUS_MODE.TRADITIONAL
      ? [
          SHEET_TYPE.EXAMINATION_AVERAGE_RESULT,
          SHEET_TYPE.ATTENDANCE_RATE,
          SHEET_TYPE.EXAMINATION_RATE,
        ]
      : [SHEET_TYPE.ATTENDANCE_RATE, SHEET_TYPE.EXAMINATION_RATE];

  const client = useApolloClient();

  const fetchCourseSectionReportData = async () => {
    try {
      const { data } = await client.query({
        query:
          mode === SYLLABUS_MODE.TRADITIONAL ? getCourseSectionReport : getTKCourseSectionReport,
        variables: {
          schoolTermId,
          schoolGradeId,
          subjectId,
          courseCode,
          schoolCode,
        },
      });
      return { data };
    } catch (error) {
      return { error };
    }
  };

  const exportCourseSectionReport = async ({ report: courseSectionReport, type }) => {
    const {
      schoolGrade: { shortName: schoolGrade } = {},
      schoolTerm: { shortName: schoolTerm } = {},
      subject: { name: subject } = {},
      course: { courseTitle } = {},
    } = courseSectionReport;

    const formattedReportAOAHeader = formatReportToAOAHeader(courseSectionReport, mode);
    const workbook = XLSX.utils.book_new();
    SHEETS.forEach(sheetType => {
      const formattedReportAOAData = convertCourseSectionReportToAOA(
        courseSectionReport,
        sheetType,
        mode,
      );
      const worksheet = createAndFormatWorksheet(
        [...formattedReportAOAHeader, ...formattedReportAOAData],
        mode,
        courseSectionReport,
      );
      XLSX.utils.book_append_sheet(workbook, worksheet, SHEET_NAME[sheetType]);
    });

    XLSX.writeFile(
      workbook,
      `ภาพรวมระดับชั้น_${formatSchoolTerm(
        schoolTerm,
      )}_${schoolGrade}-ทุกห้อง_${subject}_${courseTitle}.` + (type || 'xlsx'),
    );
  };

  return { fetchCourseSectionReportData, exportCourseSectionReport };
};

export default ExportCourseSectionReportData;
